<template>
  <div class="itemlist-wrapper">
    <DataTable
      :key="`${visibleData.length}-${storageKey || 'itemlist'}`"
      v-model:selection="selectedItems"
      breakpoint="0px"
      :value="visibleData"
      class="itemlist"
      @rowClick="onRowClick"
      :rowStyle="() => ({ backgroundColor: '#53435f' })"
      :dataKey="'id'"
      scrollable
      :scrollHeight="scrollHeight || '100vh'"
      :highlightOnSelect="true"
      stateStorage="local"
      :stateKey="storageKey"
      @update:sortField="onSortChange"
      @update:sortOrder="onSortOrderChange"
      :rowClass="getRowClass"
      :virtual-scroller-options="{ itemSize: 88 }"
    >
      <template #empty>
        <div v-if="!props.loader" class="empty-message">
          <p>No search results found</p>
          <p>We can't find any item matching your search.</p>
        </div>
        <div v-else>
          <Skeleton width="100%" height="40rem" />
        </div>
        <span></span>
      </template>

      <Column
        v-if="!isArchive"
        selectionMode="multiple"
        aria-label="Select row"
      >
      </Column>

      <Column
        v-for="col of tableColumns"
        :icon="col.icon"
        :sortable="col.sortable"
        :class="col.class"
        :key="col.id"
        :field="col.field"
        :dataType="col.dataType"
        :header="col.icon ? '' : col.header"
        :style="`width: ${col.width}`"
      >
        <template #sorticon="{ sortOrder }">
          <div>
            <img
              :src="getSortIcon(sortOrder)"
              alt="SortIcon"
              class="itemlist-sorticon"
            />
          </div>
        </template>

        <template v-if="props.loader" #body="col">
          <Skeleton :width="col.data.width" height="2.5rem" />
        </template>

        <!-- code for column headed title -->
        <template v-else-if="col.header === 'TITLE'" #body="col">
          <div
            :aria-label="col.data.type === 'folder' ? 
            `${col.data.type} titled ${col.data.title} containing ${formatSessionsCount(col.data.sessions.length)} sessions` :
            `${col.data.type} titled ${col.data.title}, duration ${formatDuration(col.data.uploads?.find((upload: any) => upload.type !== 'mp-supplementary')?.duration)}.`
            "
            :aria-details="`Activate to open the ${col.data.type}`"
            class="itemlist-column-item itemlist-type-title"
            @click="onRowClick({ data: col.data })"
            @keydown="
              ($event) => {
                if ($event.key === 'Enter') {
                  $event.preventDefault();
                  onRowClick({ data: col.data });
                }
              }
            "
            tabindex="0"
            aria-role="button"
          >
            <div
              aria-hidden="true"
              v-if="col.data.type === 'folder'"
              class="itemlist-type-icon itemlist-type-icon-folder"
            >
              <img :src="FolderIconImage" alt="Folder" />
            </div>
            <div
              aria-hidden="true"
              v-if="col.data.type === 'session'"
              class="itemlist-type-icon itemlist-type-icon-session"
            >
              <img :src="AudioIconImage" alt="Media Session" />
            </div>

            <div aria-hidden="true" class="itemlist-type-title-title">
              <div class="itemlist-type-title-title-title">
                {{ formatTitle(col.data.title) }}
              </div>
              <div
                v-if="col.data.type === 'session'"
                class="itemlist-type-title-title-duration"
              >
                <img :src="ClockIcon" alt="ClockIcon" />
                {{formatDuration(col.data.uploads?.find((upload: any) => upload.type !== 'mp-supplementary')?.duration)}}
              </div>
              <div
                v-if="col.data.type === 'folder'"
                class="itemlist-type-title-title-duration"
              >
                <img :src="SmallSessionIcon" alt="SessionSmallIcon" />
                {{ formatSessionsCount(col.data.sessions.length) }}
              </div>
            </div>
          </div>
        </template>

        <!-- code for column headed supplementary  -->
        <template v-else-if="col.header === 'SUPPLEMENTARY FILES'" #body="col">
          <!-- code for column headed supplementary if folder -->
          <div v-if="col.data.type !== 'session'" class="itemlist-column-text">
            {{ "-" }}
          </div>

          <!-- code for column headed supplementary if session -->
          <div
            v-else
            class="itemlist-column-item itemlist-column-text supplementary-files"
          >
            <UploadButton
              :type="'supplementaryFiles'"
              :additionalUploads="col.data.uploads"
              :sessionId="col.data.id"
              aria-label="Upload supplementary files"
            />
            <div class="itemlist-fileupload">
              <span class="fileupload-count">{{
                countUpload(col.data.uploads)
              }}</span
              >/5 files
            </div>
          </div>
        </template>

        <template v-else-if="col.header === 'NOTES STATUS'" #body="col">
          <div
            class="tag"
            :class="col.data.noteStatus"
            style="max-width: min-content"
            :aria-label="`Notes status: ${
              col.data.noteStatus ? col.data.noteStatus : ''
            }`"
          >
            <div
              v-if="
                col.data.noteStatus === '' ||
                col.data.noteStatus === 'no-value' ||
                !col.data.noteStatus
              "
              class="itemlist-column-text"
            >
              {{ "-" }}
            </div>
            <div v-else>
              {{ col.data.noteStatus }}
            </div>
          </div>
        </template>

        <template v-else-if="col.header === '' && !isArchive" #body="col">
          <IconButton
            :icon="ThreeDotsWhiteIcon"
            :alt="'Settings'"
            v-tooltip.top="'Settings'"
            xsmall
            @click="(e: Event) => toggleSessionMenu(e, col.data)"
            :enterPress="(e: Event) => toggleSessionMenu(e, col.data)"
            aria-haspopup="true"
            ariaLabel="Settings"
          />
          <Menu
            ref="menu"
            id="actions_menu"
            class="table-menu-popup"
            :model="PopupMenus"
            :popup="true"
            :tabindex="0"
            :ariaLabel="
              col.data.type === 'session' ? 'Session actions' : 'Folder actions'
            "
          />
        </template>

        <template v-else-if="col.header === '' && isArchive" #body="col">
          <Button
            @click="restoreItem(col.data)"
            class="itemlist-button"
            @keydown="
              ($event) => {
                if ($event.key === 'Enter') {
                  $event.preventDefault();
                  restoreItem(col.data);
                }
              }
            "
            :aria-label="'Restore'"
          >
            <img :src="RestoreIcon" alt="RestoreIcon" />
            Restore
          </Button>
        </template>

        <template v-else #body="col">
          <div class="itemlist-column-text created-at" style="width: 100%">
            {{
              col.field && typeof col.field === "string"
                ? formatDateToCurrentLocale(col.data[col.field], true)
                : "-"
            }}
          </div>
        </template>
      </Column>
    </DataTable>
  </div>

  <div class="itemlist-viewmore-button-wrapper">
    <button
      v-if="canLoadMore"
      @click="loadMore"
      @keydown="
        ($event) => {
          if ($event.key === 'Enter') {
            $event.preventDefault();
            loadMore();
          }
        }
      "
      class="itemlist-viewmore-button"
      aria-label="View more"
    >
      View more
    </button>
  </div>

  <Toast
    position="top-center"
    group="notification"
    class="itemlist-notification-toast-wrapper"
  ></Toast>
</template>

<script setup lang="ts">
import { ref, useTemplateRef, computed, watch, nextTick } from "vue";
import { formatDateToCurrentLocale } from "@/assets/helpers";
import Skeleton from "primevue/skeleton";
import UploadButton from "../UploadButton.vue";
import IconButton from "../IconButton.vue";
import { SessionWithOrdersAndType } from "../../types/session-interfaces";
import { FolderWithSessions } from "../../types/folder-interfaces";
import {
  ItemListMenuProp,
  ItemListColumn,
} from "../../types/itemlist-interfaces";
import { convertSecondsToMS } from "../../assets/helpers_legacy.js";
import { useStore } from "vuex";
import { useToast } from "primevue/usetoast";

const ThreeDotsWhiteIcon =
  require("../../assets/images/ThreeDotsWhiteIcon.svg") as string;
const FolderIconImage =
  require("../../assets/images/FolderDarkIcon.svg") as string;
const AudioIconImage = require("../../assets/images/AudioIcon.svg") as string;
const ClockIcon = require("../../assets/images/ClockSmall.svg") as string;
const SmallSessionIcon =
  require("../../assets/images/SmallSessionIcon.svg") as string;
const SortIcon = require("../../assets/images/SortIcon.svg") as string;
const SortIconDescending =
  require("../../assets/images/SortIconDescending.svg") as string;
const SortIconAscending =
  require("../../assets/images/SortIconAscending.svg") as string;
const RestoreIcon = require("../../assets/images/Restore-black.svg") as string;

const store = useStore(),
  toast = useToast();

const props = defineProps<{
  loader?: boolean;
  columns: ItemListColumn[];
  data: (SessionWithOrdersAndType | FolderWithSessions)[];
  sessionMenuProps?: ItemListMenuProp[];
  folderMenuProps?: ItemListMenuProp[];
  lazyLoading: boolean;
  canLoadMore?: boolean;
  onLazyLoad?: Function;
  openFolder?: Function;
  openSession: Function;
  isArchive?: boolean;
  scrollHeight?: string;
  // eslint-disable-next-line no-unused-vars
  onSortChange?: (sortField: string) => void;
  // eslint-disable-next-line no-unused-vars
  onSortOrderChange?: (sortOrder: number | undefined) => void;
  storageKey?: string;
}>();

const selectedItems = computed({
  get: () => store.getters["itemList/getSelectedItems"],
  set: (val) => store.dispatch("itemList/updateSelection", val),
});

const onRowClick = async (event: {
  data: SessionWithOrdersAndType | FolderWithSessions;
}) => {
  if (event.data.type === "folder") {
    if (props.openFolder) {
      props.openFolder(event.data);
    }
  } else {
    props.openSession(event.data);
  }
};

function countUpload(uploadsArray: []) {
  const filteredUploads = uploadsArray.filter(
    (upload: any) => upload.type === "mp-supplementary"
  );
  return filteredUploads.length;
}

function formatTitle(title: string) {
  if (title.length > 48) {
    return title.slice(0, 48) + "...";
  }
  return title;
}

interface MenuItemBase {
  label?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  command?: () => void;
  // eslint-disable-next-line no-unused-vars
  keyHandler?: (event: KeyboardEvent) => void;
  icon?: string;
  disabled?: boolean;
  visible?: boolean;
  class?: any;
}

type MenuItem = MenuItemBase;

const PopupMenus = ref<MenuItem[]>([]);
const menuRef = useTemplateRef("menu");

const visibleCount = ref(0);

watch(
  () => props.data,
  function () {
    visibleCount.value = props.data.length ?? itemsPerPage;
  },
  { once: false, immediate: true }
);
const itemsPerPage = 10;

const menuKeydownBound = ref(false);

const visibleData = computed(() => {
  if (props.loader) {
    // when the prop is loading, fill the data with dummy rows for the Skeleton
    // component to render
    return Array(5).fill({});
  } else if (props.data) {
    if (props.isArchive) {
      // show all archived items
      return props.data;
    } else {
      return props.data.slice(0, visibleCount.value);
    }
  } else {
    return [];
  }
});

// fill in with a Loading column to load in Skeleton component when the column
// is loading in
const tableColumns = computed(() => {
  return props.columns?.length
    ? props.columns
    : [{ header: "Loading...", field: "loading", width: "100%" }];
});

const canLoadMore = computed(() => {
  if (props.lazyLoading) {
    return props.canLoadMore || visibleData.value.length < props.data.length;
  } else {
    return visibleData.value.length < props.data.length;
  }
});

async function loadMore() {
  if (props.lazyLoading) {
    if (props.onLazyLoad) {
      await props.onLazyLoad();
    }
  }

  if (visibleCount.value + itemsPerPage <= props.data.length) {
    visibleCount.value += itemsPerPage;
  } else {
    visibleCount.value = props.data.length;
  }
}

function formatDuration(durationValue: number | null | undefined): string {
  if (durationValue && durationValue !== 0) {
    return convertSecondsToMS(durationValue / 1000);
  }
  return "-";
}

function getMenuRoot(): HTMLElement | null {
  // Try PrimeVue instance overlay first
  const inst = Array.isArray(menuRef.value)
    ? (menuRef.value[0] as any)
    : (menuRef.value as any);

  const cand = inst?.overlay ?? inst?.$el ?? null;
  if (cand && typeof (cand as any).querySelectorAll === "function") {
    return cand as HTMLElement;
  }

  // Prefer your class-qualified overlay
  const overlays = document.querySelectorAll(
    ".table-menu-popup.p-menu-overlay"
  );
  if (overlays.length) return overlays[overlays.length - 1] as HTMLElement;

  // Fallback to generic PrimeVue overlay
  const generic = document.querySelectorAll(".p-menu-overlay");
  return generic.length ? (generic[generic.length - 1] as HTMLElement) : null;
}

function getMenuItems(): HTMLElement[] {
  const root = getMenuRoot();
  if (!root) return [];
  return Array.from(
    root.querySelectorAll(
      ".p-menu-item:not(.p-disabled) .p-menu-item-link, " +
        ".p-menu-item:not(.p-disabled) a, " +
        ".p-menu-item:not(.p-disabled) button"
    )
  ) as HTMLElement[];
}

function bindMenuKeyboardOnce() {
  const root = getMenuRoot();
  if (!root || menuKeydownBound.value) return;
  root.addEventListener("keydown", onMenuKeydown);
  menuKeydownBound.value = true;
}

function focusFirstMenuItem() {
  const items = getMenuItems();
  items[0]?.focus();
}

function onMenuKeydown(e: KeyboardEvent) {
  const items = getMenuItems();
  if (!items.length) return;

  const active = document.activeElement as HTMLElement;

  let i = Math.max(0, items.indexOf(active));

  const move = (next: number) =>
    items[(next + items.length) % items.length].focus();

  switch (e.key) {
    case "ArrowDown":
      e.preventDefault();
      move(i + 1);
      break;
    case "ArrowUp":
      e.preventDefault();
      move(i - 1);
      break;
    case "Tab":
      e.preventDefault();
      if (e.shiftKey) move(i - 1);
      else move(i + 1);
      break;
    case "Home":
      e.preventDefault();
      move(0);
      break;
    case "End":
      e.preventDefault();
      move(items.length - 1);
      break;
    case "Enter":
    case " ":
      e.preventDefault();
      active?.click();
      break;
    case "Escape": {
      const inst = Array.isArray(menuRef.value)
        ? (menuRef.value[0] as any)
        : (menuRef.value as any);
      inst?.hide?.();
      break;
    }
  }
}

async function toggleSessionMenu(
  event: Event,
  rowData: FolderWithSessions | SessionWithOrdersAndType
) {
  const selectedMenuProps =
    rowData.type === "session" ? props.sessionMenuProps : props.folderMenuProps;

  if (!selectedMenuProps?.length) return;

  PopupMenus.value = selectedMenuProps.map((item) => {
    const base: MenuItem = {};

    if (item.label) base.label = item.label;
    if (item.icon) base.icon = item.icon + " itemlist-popupmenu";
    if (item.class) base.class = item.class;
    if (item.ariaLabel) base.ariaLabel = item.ariaLabel;
    if (item.command) base.command = () => item.command(rowData);

    if (item.checkLabel) base.label = item.checkLabel(rowData);
    if (item.checkVisible) base.visible = item.checkVisible(rowData);
    if (item.checkDisable) base.disabled = item.checkDisable(rowData);

    return base;
  });

  const inst = Array.isArray(menuRef.value)
    ? (menuRef.value[0] as any)
    : (menuRef.value as any);
  inst?.toggle?.(event);

  // Wait for overlay teleport + mount
  await nextTick();
  requestAnimationFrame(() => {
    bindMenuKeyboardOnce();
    focusFirstMenuItem();
  });
}

function isSelected(row: SessionWithOrdersAndType | FolderWithSessions) {
  return selectedItems.value.some((r: { id: string }) => r.id === row.id);
}

function isSelectable(
  row: SessionWithOrdersAndType | FolderWithSessions
): boolean {
  const type = selectedItems.value[0]?.type;
  return !type || row.type === type || isSelected(row);
}

function getSortIcon(order: number) {
  if (order === 1) {
    return SortIconAscending;
  }
  if (order === -1) {
    return SortIconDescending;
  }

  return SortIcon;
}
function formatSessionsCount(numSessions: number | null | undefined) {
  if (!numSessions) {
    return `0 session`;
  } else if (numSessions <= 1) {
    return `${numSessions} session`;
  } else {
    return `${numSessions} sessions`;
  }
}

function getRowClass(row: SessionWithOrdersAndType | FolderWithSessions) {
  return {
    "row-unselectable": !isSelectable(row),
  };
}

async function restoreItem(
  data: SessionWithOrdersAndType | FolderWithSessions
) {
  await store.dispatch("itemList/unarchiveItem", data.id);
  toast.add({
    severity: "secondary",
    summary: `Item restored successfully.`,
    life: 3000,
    group: "notification",
  });
}

defineExpose({
  toggleSessionMenu,
  visibleCount,
});
</script>

<style lang="scss">
.itemlist-viewmore-button-wrapper {
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;

  .itemlist-viewmore-button {
    @include callout;
    width: 20.8rem;
    height: 4.4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: map-get($colours, accent-purple-3);
    color: #ffffff4c;
    border: solid 0.1rem #ffffff4c;
    border-radius: 2.4rem;
  }
}

.itemlist-wrapper {
  padding: 1rem;
  width: 100%; // Ensures the container fills the available width
  flex: 1;

  .itemlist {
    width: 100%; // Ensures the DataTable itself takes up 100% of the available width
    border: none;
    margin: 0 auto; // Centers the table within the container
    border: none;

    .itemlist-sorticon {
      scale: 50%;
    }

    /* Style the header background and border-radius */
    .p-datatable-thead > tr {
      background-color: transparent;
      border-radius: 4px;
      /* Border radius of 4px */
      overflow: hidden;
      /* Ensure the border-radius is applied correctly */
    }

    .p-datatable-thead > tr > th {
      background-color: transparent;
      color: white;
      /* Text color for header */
      text-align: center;
      /* Center-align the text */
      padding: 12px;
      /* Padding inside the header */
      font-weight: bold;
      /* Make header text bold */
      border: none;
      font-size: 1.4rem;
    }

    .p-datatable-header {
      background: green;
    }

    .itemlist-column-item {
      display: flex;
      flex-direction: row;
      align-items: center;
      width: 100%;
      height: 100%;

      &:focus {
        box-shadow: 0 0 0 0.3rem rgba(255, 233, 254, 0.4);
        outline: solid 0.2rem rgba(255, 233, 254, 1);
        border: solid 0.1rem map-get($colours, accent-purple-3);
      }
    }

    .p-datatable-thead,
    .p-datatable-tbody {
      display: table; // Ensure both header and body behave like a table
      width: 100%; // Ensure both take up 100% of the container's width
      border: none;
    }

    .p-datatable-tbody {
      > tr {
        width: 100%; // Ensures the row takes the full width
        margin-top: 1.75rem; // Adds space above each row
        margin-bottom: 1.75rem; // Adds space below each row
        background-color: map-get(
          $colours,
          accent-purple-2
        ); // White background for rows
        border-radius: 1.6rem; // Optional: adds rounded corners to rows
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); // Optional: light shadow for rows
        display: flex; // Use flexbox to position columns
        justify-content: space-between;
        align-items: center;
        height: 8.8rem;
        align-self: stretch;
        gap: 1.6rem; // Adds space between columns
        border: none;
        cursor: pointer;
      }

      > tr.p-datatable-row-selected {
        border: solid 0.1rem #b076e7;
      }

      .p-checkbox {
        margin-left: 2rem;
        /* Adjusts the checkbox margin */
        --p-checkbox-icon-size: var(--p-checkbox-icon-lg-size);
        --p-checkbox-width: var(--p-checkbox-lg-width);
        --p-checkbox-height: var(--p-checkbox-lg-height);

        &:focus-within {
          box-shadow: 0 0 0 0.3rem rgba(255, 233, 254, 0.4);
          outline: solid 0.2rem rgba(255, 233, 254, 1);
          border: solid 0.1rem map-get($colours, accent-purple-3);
        }
      }

      .p-checkbox-checked .p-checkbox-box {
        background-color: #b076e7;
        /* Background color when checked */
        border-color: #b076e7;
        /* Border color when checked */

        &:focus-within {
          box-shadow: 0 0 0 0.3rem rgba(255, 233, 254, 0.4);
          outline: solid 0.2rem rgba(255, 233, 254, 1);
          border: solid 0.1rem map-get($colours, accent-purple-3);
        }
      }
    }

    .p-datatable-column-header-content {
      display: flex !important;
      justify-content: space-between;
      align-items: center;
      width: 100%;

      .p-checkbox {
        margin-left: 1.2rem;
        /* Adjusts the checkbox margin */
        --p-checkbox-width: var(--p-checkbox-lg-width);
        --p-checkbox-height: var(--p-checkbox-lg-height);

        &:focus-within {
          box-shadow: 0 0 0 0.3rem rgba(255, 233, 254, 0.4);
          outline: solid 0.2rem rgba(255, 233, 254, 1);
          border: solid 0.1rem map-get($colours, accent-purple-3);
        }
      }

      .p-checkbox-checked .p-checkbox-box {
        background-color: #b076e7;
        /* Background color when checked */
        border-color: #b076e7;
        /* Border color when checked */

        &:focus-within {
          box-shadow: 0 0 0 0.3rem rgba(255, 233, 254, 0.4);
          outline: solid 0.2rem rgba(255, 233, 254, 1);
          border: solid 0.1rem map-get($colours, accent-purple-3);
        }
      }
    }

    .p-datatable .p-datatable-tbody > tr.p-highlight {
      background-color: inherit !important;
      color: inherit !important;
    }

    .p-datatable-tbody td {
      padding: 0.5rem; // Adds space inside each cell
      text-align: left; // Align text to the left for better readability
      height: 100%;
      // width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      border: none;

      .itemlist-button {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0 0.8rem 0 0.8rem;
        padding: 0.8rem 1.6rem;
        font-size: 1.4rem;
        border-radius: 0.8rem;

        img {
          width: 1.6rem;
          height: 1.6rem;
        }

        &:disabled {
          background-color: #e0e0e0;
          color: #a0a0a0;
          cursor: not-allowed;
          pointer-events: none;

          img {
            opacity: 0.5;
          }
        }
      }
    }

    .itemlist-type-title {
      @include section-sub-header;
      color: #ffffff;
      font-weight: bold;

      &:focus {
        box-shadow: 0 0 0 0.3rem rgba(255, 233, 254, 0.4);
        outline: solid 0.2rem rgba(255, 233, 254, 1);
        border: solid 0.1rem map-get($colours, accent-purple-3);
      }

      &-icon {
        margin: 0 2.15rem 0 3.15rem;
      }

      &-title {
        display: flex;
        flex-direction: column;

        &-title {
          display: -webkit-box;
          line-clamp: 2;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: break-word;
        }

        &-duration {
          @include caption;
          display: flex;
          flex-direction: row;
          align-items: center;
          font-weight: normal;
          color: #d1d1d1;

          img {
            width: 1.2rem;
            height: 1.2rem;
            fill: white;
            margin-right: 0.5rem;
          }
        }
      }
    }

    .itemlist-type-icon {
      width: 4.8rem;
      height: 4.8rem;
      margin: 0 1.6rem 0;
      padding: 1.2rem;
      border-radius: 0.8rem;
    }

    .itemlist-type-icon-folder {
      background-color: #d4ffda;
    }

    .itemlist-type-icon-session {
      background-color: #f0dcff;
    }

    .itemlist-column-text {
      &.supplementary-files {
        @include callout;
        color: map-get($colours, grey-2);
        display: flex;
        gap: 1.6rem;

        .fileupload-count {
          color: map-get($colours, white);
        }
      }

      &.created-at {
        @include section-sub-header;
        width: 100%;
        padding-left: 0.6rem;
      }
    }

    .itemlist-fileupload {
      border-radius: 0.8rem;
      font-size: 1.2rem;
    }
  }

  .table-menu-popup {
    width: fit-content;

    &.actions_menu_list {
      min-width: 300px;
    }

    &:focus-visible {
      border: solid 2px map-get($colours, accent-purple);
      outline: solid 1px rgba(255, 233, 254, 40);
      box-shadow: 0 0 0 2.5px rgba(255, 233, 254, 0.4);
    }

    &.p-menu-overlay {
      background-color: red;

      .p-menu-item {
        &:hover,
        &:active,
        .p-menu-item-link {
          .p-menu-item-icon,
          .p-menu-item-label {
            @include body-2;
            color: map-get($colours, white);
          }
        }
      }
    }
  }

  .tag {
    font-size: 1rem;
    text-align: center;
    border-radius: 1.6rem;
    min-width: min-content;
    background-color: transparent;
    color: #303030;
    padding: 4px 8px;
    text-transform: uppercase;
    @include caption-small;
    font-weight: 600;
    margin: 0;
    overflow: hidden;
    white-space: nowrap;

    // TODO: update updated design
    &.done,
    &.completed {
      background: #b0ddff;
      border-color: black;
    }

    &.requested,
    &[class*="pending"] {
      background: rgba(255, 255, 255, 0.6);
    }
    &[class*="in-progress"] {
      background: #e4ffe5;
    }

    &.error,
    &[class*="cancelled"] {
      background: #fbd5d3;
      color: #b00020;
    }

    &.no-value {
      visibility: hidden;
    }

    &.requested {
      background: #fff3c8;
    }
  }
}

.itemlist-threedoticon-svg-icon {
  background: url("../../assets/images/ThreeDotsWhiteIcon.svg");
  height: 2rem;
  width: 2rem;
}

.itemlist-upload-svg-icon {
  background: url("../../assets/images/Upload-black.svg");
  height: 2rem;
  width: 2rem;
}

.itemlist-popupmenu {
  height: 1.6rem;
  width: 1.6rem;
}

.itemlist-notification-toast-wrapper {
  margin: 0;
  padding: 0;

  .p-toast-message-content {
    margin: 0;
    padding: 0;
    background: map-get($colours, accent-purple-4);
    font-size: 1.4rem;
  }

  .p-toast-message-icon {
    margin: 0;
    padding: 0;
    width: 0;
    height: 0;
  }
}

.itemlist-toast-single-item-width {
  width: 56.5rem;
}

.itemlist-toast-multiple-item-width {
  width: 39rem;
}

.row-unselectable {
  pointer-events: none;
  opacity: 0.5;
}
</style>
